// Authenticated layout. Bounces to /login without a session; then runs
// the ONE environment probe and the sandbox guard before anything else
// mounts. A refusal renders only the blocking full-page error — the
// sidebar, top bar and every page (and so every page's queries) never
// mount. Shell shape is web-market-maker's: sidebar + main column
// (sticky top bar + scrolling content), drawer below `lg`.
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { BlockingError } from "@/components/BlockingError";
import { Sidebar } from "@/components/layout/Sidebar";
import { TopBar } from "@/components/layout/TopBar";
import { API_BASE } from "@/lib/api";
import { fetchMeOrNull, fetchers, keys } from "@/lib/queries";
import { evaluateSandbox, type GuardVerdict } from "@/lib/sandboxGuard";
import type { Environment } from "@/lib/types";

export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context, location }): Promise<{ guard: GuardVerdict }> => {
    const me = await context.queryClient.ensureQueryData({
      queryKey: keys.me(),
      queryFn: fetchMeOrNull,
      staleTime: 5_000,
    });
    if (!me) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    let env: Environment | null = null;
    try {
      env = await context.queryClient.ensureQueryData({
        queryKey: keys.environment(),
        queryFn: fetchers.environment,
        staleTime: 10_000,
      });
    } catch (e) {
      if ((e as { status?: number }).status === 401) {
        context.queryClient.setQueryData(keys.me(), null);
        throw redirect({ to: "/login", search: { redirect: location.href } });
      }
      env = null;
    }
    return { guard: evaluateSandbox({ hostname: window.location.hostname, apiBase: API_BASE, environment: env }) };
  },
  component: AppLayout,
});

function AppLayout() {
  const { guard } = Route.useRouteContext();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  if (!guard.ok) return <BlockingError reason={guard.reason} message={guard.message} />;
  return (
    <div className="admin-shell">
      <Sidebar mobileOpen={mobileNavOpen} onMobileClose={() => setMobileNavOpen(false)} />
      <div className="admin-main">
        <TopBar onMenuClick={() => setMobileNavOpen(true)} />
        <main className="admin-main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
